import React from 'react';
import ShowcaseReel from './ShowcaseReel';
import PhoneStack from './PhoneStack';

// FIND BRAND VOICE PAGE
export const findbrandvoiceContent = {
  pageMaxWidth: '1200px',
  breadcrumb: {
    text: 'Back to Home',
    url: '/'
  },

  sections: [
    { id: 'intro-card', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="findbrandvoice" card="https://findbrandvoice.com" alt="Find Brand Voice" /> }] },
    {
      id: 'hero',
      spacing: 'clamp(32px, 5vw, 64px)',
      blocks: [
        {
          type: 'container',
          narrow: true,
          blocks: [
            {
              type: 'custom',
              render: () => (
                <span style={{
                  background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '25px',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'inline-block'
                }}>
                  Creative Tools
                </span>
              )
            }
          ]
        },
        { type: 'spacer', size: 'small' },
        { type: 'title', text: 'FindBrandVoice', size: 'h1' },
        {
          type: 'text',
          content: 'Generate taglines that actually sound like your brand.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'text',
          content: 'Built on top of Tonelab\'s 3,000+ brand archive. You go through a 5-step wizard — pick your industry, select your tone, choose brands you admire — and it generates 5 custom taglines based on your choices and the real taglines of the brands you selected.'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://findbrandvoice.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Try FindBrandVoice
              </a>
              <span style={{
                background: 'var(--bg-accent)',
                color: 'var(--text-accent)',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                Live
              </span>
            </div>
          )
        }
      ]
    },
    { id: 'showcase-motion', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="findbrandvoice" video alt="Find Brand Voice" /> }] },
    { id: 'showcase-live-demo', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="findbrandvoice" frames={[1, 2]} alt="Find Brand Voice" /> }] },

    // LIVE EMBED
    {
      id: 'live-demo',
      title: 'Try It Live',
      blocks: [
        {
          type: 'text',
          content: 'Go through the wizard below — enter your brand, pick your tone, select brands you like, and generate your taglines.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              minHeight: '600px',
              background: '#000',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '2px solid var(--border-color)'
            }}>
              <iframe
                src="https://findbrandvoice.com"
                title="FindBrandVoice Live"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )
        }
      ]
    },
    { id: 'showcase-site-pages', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="findbrandvoice" frames={[3]} alt="Find Brand Voice" /> }] },

    {
      id: 'site-pages',
      title: 'Explore the site',
      blocks: [{ type: 'custom', render: () => <PhoneStack baseUrl="https://findbrandvoice.com" imgBase="/portfolio-screens/findbrandvoice/site/" pages={[
        { file: 'home', label: 'Home', path: '/' },
        { file: 'wizard', label: 'Find your voice', path: '/wizard' }
      ]} /> }]
    },

    // APP SCREENSHOTS
    {
      id: 'screenshots',
      title: 'Inside Find Brand Voice',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const screens = [
              { src: '/portfolio-screens/findbrandvoice/1.png', label: 'Brand Info' },
              { src: '/portfolio-screens/findbrandvoice/2.png', label: 'Tone & Personality' },
              { src: '/portfolio-screens/findbrandvoice/3.png', label: 'Brand Matches' },
              { src: '/portfolio-screens/findbrandvoice/4.png', label: 'Generate Taglines' }
            ];
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                {screens.map((screen, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <img
                      src={screen.src}
                      alt={screen.label}
                      style={{
                        width: '100%',
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
                      }}
                    />
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.75rem',
                      marginTop: '8px'
                    }}>
                      {screen.label}
                    </p>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },

    // HOW IT WORKS
    {
      id: 'how-it-works',
      title: 'How It Works',
      blocks: [
        {
          type: 'text',
          content: 'A 5-step guided wizard that narrows down your brand voice step by step:',
          size: 'normal',
          align: 'left'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => {
            const steps = [
              {
                num: '01',
                title: 'Brand Info',
                desc: 'Enter your brand name, industry, and what you do'
              },
              {
                num: '02',
                title: 'Tone & Style',
                desc: 'Select tone words that match how you want your brand to sound'
              },
              {
                num: '03',
                title: 'Brand Inspiration',
                desc: 'Pick brands you admire from 2,000+ real examples — their taglines feed directly into the generation'
              },
              {
                num: '04',
                title: 'Tagline Style',
                desc: 'Choose your format — command, aspirational, emotional, or descriptive'
              },
              {
                num: '05',
                title: 'Generate',
                desc: '5 custom taglines generated using Claude, based on everything you selected'
              }
            ];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {steps.map((step, i) => (
                  <div key={i} style={{
                    display: 'grid',
                    gridTemplateColumns: '44px 1fr',
                    gap: '14px',
                    alignItems: 'center',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <span style={{
                      background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                      color: 'white',
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {step.num}
                    </span>
                    <div>
                      <h4 style={{
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        fontWeight: '600',
                        margin: '0 0 4px 0'
                      }}>
                        {step.title}
                      </h4>
                      <p style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.9rem',
                        margin: 0,
                        lineHeight: '1.5'
                      }}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },
    { id: 'showcase-tonelab-connection', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="findbrandvoice" frames={[5]} alt="Find Brand Voice" /> }] },

    // CONNECTION TO TONELAB
    {
      id: 'tonelab-connection',
      title: 'Built on Tonelab',
      blocks: [
        {
          type: 'container',
          background: true,
          blocks: [
            {
              type: 'text',
              content: 'The 3,000+ brands in Tonelab aren\'t just a database — they\'re the engine behind FindBrandVoice. When you pick brands you admire in the wizard, their actual taglines, tone words, and messaging styles get passed into the prompt. So the AI isn\'t generating from nothing — it\'s working from real brand examples you chose.'
            },
            { type: 'spacer', size: 'small' },
            {
              type: 'custom',
              render: () => (
                <a
                  href="/projects/tonelab"
                  style={{
                    color: 'var(--text-accent)',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                >
                  See Tonelab →
                </a>
              )
            }
          ]
        }
      ]
    },

    // TECH STACK
    {
      id: 'tech',
      title: 'Tech Stack',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px'
            }}>
              {[
                { category: 'App', items: ['React Native', 'Expo SDK 54', 'TypeScript', 'Expo Router'] },
                { category: 'Backend', items: ['Firebase Auth & Firestore', 'Claude (Anthropic API)', 'Vercel Serverless Functions'] },
                { category: 'Platform', items: ['Web', 'iOS', 'Android'] },
                { category: 'Data', items: ['3,000+ brand database', 'Brand logos & metadata', 'AsyncStorage + Firestore'] }
              ].map((group, i) => (
                <div key={i} style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    color: 'var(--text-accent)',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px'
                  }}>
                    {group.category}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {group.items.map((item, j) => (
                      <span key={j} style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.9rem'
                      }}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      ]
    }
  ],

  callToAction: {
    endCard: '/showcase/findbrandvoice/end.webp',
    brand: { bg: "linear-gradient(160deg, #ABCDE8 0%, #7199C0 100%)", fg: "#0F172A", btnBg: "#111827", btnFg: "#FFFFFF", line: "rgba(15,23,42,0.35)" },
    title: 'FindBrandVoice',
    text: 'Generate taglines based on 3,000+ real brands',
    button: { text: 'Try It →', url: 'https://findbrandvoice.com' }
  }
};